import { graphql } from 'react-relay';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useSpaceManagementOperationsSpacePropertiesQuery as useSpaceManagementOperationsSpacePropertiesQuery$data,
	useSpaceManagementOperationsSpacePropertiesQueryVariables as useSpaceManagementOperationsSpacePropertiesQuery$variables,
} from './__types__/useSpaceManagementOperationsSpacePropertiesQuery.ts';
import { useSpaceRoleMode } from './useSpaceRoleMode.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const useSpaceManagementOperationsSpacePropertiesQuery = graphql`
	query useSpaceManagementOperationsSpacePropertiesQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
			operations {
				operation
				targetType
			}
		}
	}
`;
interface SpaceManagementOperations {
	canManageSomeUsers: boolean;
	canManageUsers: boolean;
	canManageNonlicensedUsers: boolean;
	canManageGuestUsers: boolean;
	canManagePublicLinks: boolean;
	canManageTemplates: boolean;
	canManageLookAndFeel: boolean;
	canManageContent: boolean;
	canAdminister: boolean;
	canArchive: boolean;
	canCopy: boolean;
	loading: boolean;
}

export const MANAGE_USERS = 'manage_users';
export const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
export const MANAGE_GUEST_USERS = 'manage_guest_users';
export const MANAGE_PUBLIC_LINKS = 'manage_public_links';
export const MANAGE_TEMPLATES = 'manage_templates';
export const ADMINISTER = 'administer';
export const MANAGE_LOOK_AND_FEEL = 'manage_look_and_feel';
export const MANAGE_CONTENT = 'manage_content';
export const ARCHIVE = 'archive';
export const COPY = 'copy';

export type SpaceOperation =
	| typeof MANAGE_USERS
	| typeof MANAGE_NONLICENSED_USERS
	| typeof MANAGE_GUEST_USERS
	| typeof MANAGE_PUBLIC_LINKS
	| typeof MANAGE_TEMPLATES
	| typeof ADMINISTER
	| typeof MANAGE_LOOK_AND_FEEL
	| typeof MANAGE_CONTENT
	| typeof ARCHIVE
	| typeof COPY;

/**
 * Operations array shape returned by the GraphQL `space.operations` field.
 * Generic so it accepts any object with `operation` and `targetType` fields,
 * regardless of the source query / generated type.
 */
export type SpaceOperationsArray =
	| ReadonlyArray<
			| {
					readonly operation?: string | null;
					readonly targetType?: string | null;
			  }
			| null
			| undefined
	  >
	| null
	| undefined;

/**
 * Standalone helper to check if a given operations array (e.g. from a GraphQL
 * `space.operations` field) grants the requested space-level operation.
 *
 * Useful when you already have the operations data from a parent query and
 * want to avoid making an extra `useSpaceManagementOperations` query per row /
 * component (e.g. in list/table views).
 */
export const hasSpaceOperation = (
	operations: SpaceOperationsArray,
	spaceOperation: SpaceOperation,
): boolean => {
	return (
		operations?.some(
			(operation) => operation?.operation === spaceOperation && operation?.targetType === 'space',
		) ?? false
	);
};

/**
 * Hook to check if the user has the required operations to manage a space.
 */
export const useSpaceManagementOperations = ({
	spaceKey,
	skip,
}: {
	spaceKey: string;
	skip?: boolean;
}): SpaceManagementOperations => {
	const { isRbacEnabled, loading: isLoadingSpaceRoleMode } = useSpaceRoleMode();
	const defaultSkip = !spaceKey || !isRbacEnabled;
	const { data, loading } = useQueryInterop<
		useSpaceManagementOperationsSpacePropertiesQuery$data,
		useSpaceManagementOperationsSpacePropertiesQuery$variables
	>(useSpaceManagementOperationsSpacePropertiesQuery, {
		variables: { spaceKey },
		skip: skip !== undefined ? skip : defaultSkip,
		onError: (error) => {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PERMISSIONS_EXPERIENCE,
				});
			}
		},
	});

	const operations = data?.space?.operations;

	return {
		canManageSomeUsers:
			hasSpaceOperation(operations, MANAGE_USERS) ||
			hasSpaceOperation(operations, MANAGE_NONLICENSED_USERS) ||
			hasSpaceOperation(operations, MANAGE_GUEST_USERS),
		canManageUsers: hasSpaceOperation(operations, MANAGE_USERS),
		canManageNonlicensedUsers: hasSpaceOperation(operations, MANAGE_NONLICENSED_USERS),
		canManageGuestUsers: hasSpaceOperation(operations, MANAGE_GUEST_USERS),
		canManagePublicLinks: hasSpaceOperation(operations, MANAGE_PUBLIC_LINKS),
		canManageTemplates: hasSpaceOperation(operations, MANAGE_TEMPLATES),
		canManageLookAndFeel: hasSpaceOperation(operations, MANAGE_LOOK_AND_FEEL),
		canAdminister: hasSpaceOperation(operations, ADMINISTER),
		canManageContent: hasSpaceOperation(operations, MANAGE_CONTENT),
		canArchive: hasSpaceOperation(operations, ARCHIVE),
		canCopy: hasSpaceOperation(operations, COPY),
		loading: loading || isLoadingSpaceRoleMode,
	};
};
